import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { AuthService } from 'src/app/api/services/auth.service';

/**
 * Where the link in the verification email lands.
 *
 * Its own screen, deliberately. Two earlier versions were wrong: the email
 * first linked straight to /api/verifyRegistration, so the customer landed on
 * the browser's raw JSON view of {"status":"verified"}; it then linked to
 * /register?token=..., which told someone their email was verified on the
 * ACCOUNT CREATION page, under a URL that says "register". Verifying an
 * address is not creating an account, so it gets its own route.
 */
@Component({
  selector: 'app-verify-email',
  standalone: true,
  imports: [CommonModule, RouterModule, MatCardModule, MatButtonModule,
            MatIconModule, MatProgressSpinnerModule, MatSnackBarModule],
  templateUrl: './verify-email.component.html',
})
export class VerifyEmailComponent implements OnInit {
  status: 'verifying' | 'success' | 'error' | 'missing' = 'verifying';
  /**
   * A bar owner's account is not usable the moment the email is confirmed:
   * the bar's documents still have to be approved. Telling them "you can sign
   * in now" sent them to a sign-in that then refused them.
   */
  isBarOwner = false;
  resending = false;
  resent = false;
  private token: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private auth: AuthService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.token = this.route.snapshot.queryParamMap.get('token');
    if (!this.token) { this.status = 'missing'; return; }
    this.auth.verifyEmail(this.token).subscribe({
      next: (res: { accountType?: string }) => {
        this.isBarOwner = res?.accountType === 'BAR_OWNER';
        this.status = 'success';
      },
      // No automatic redirect to sign-in. An earlier version navigated away
      // after three seconds, which pulled the page out from under the person
      // mid-read — and, because signing in leaves for the authorisation
      // server, a sign-in started in that window lost its credentials and
      // came back to a blank form with no error.
      error: () => { this.status = 'error'; },
    });
  }

  resend(): void {
    if (!this.token || this.resending) return;
    this.resending = true;
    this.auth.resendVerification(this.token).subscribe({
      next: () => {
        this.resending = false;
        this.resent = true;
        this.snackBar.open('A new verification link is on its way.', 'Close', { duration: 4000 });
      },
      error: () => {
        this.resending = false;
        this.snackBar.open('Could not send a new link. Please try again in a few minutes.', 'Close', { duration: 5000 });
      },
    });
  }
}
